import type { EngineInput } from '@shared/engine/pulse/core';

export interface InputSource {
  readonly kind: 'keyboard' | 'replay' | 'pointer';
  poll: (currentBeat: number) => EngineInput[];
  destroy: () => void;
}

export class KeyboardInputSource implements InputSource {
  readonly kind = 'keyboard' as const;
  private queue: EngineInput[] = [];
  private getBeatAtNow: () => number;
  private isClockRunning: () => boolean;
  private heldKeys = new Set<string>();
  private active = true;

  constructor(getBeatAtNow: () => number, isClockRunning: () => boolean = () => true) {
    this.getBeatAtNow = getBeatAtNow;
    this.isClockRunning = isClockRunning;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerCancel);
    window.addEventListener('contextmenu', this.onContextMenu);
    window.addEventListener('blur', this.onBlur);
  }

  private isFlipKey(code: string, key: string): boolean {
    return (
      code === 'Space' ||
      code === 'KeyW' ||
      code === 'ArrowUp' ||
      code === 'KeyJ' ||
      key === ' '
    );
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (!this.active) return;
    if (e.repeat) return;
    if (!this.isFlipKey(e.code, e.key)) return;
    if (this.heldKeys.has(e.code)) return;
    this.heldKeys.add(e.code);
    e.preventDefault();
    if (!this.isClockRunning()) return;
    const beat = this.getBeatAtNow();
    this.queue.push({ beat, type: 'flipDown' });
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    if (!this.active) return;
    if (!this.isFlipKey(e.code, e.key)) return;
    this.heldKeys.delete(e.code);
    if (!this.isClockRunning()) return;
    const beat = this.getBeatAtNow();
    this.queue.push({ beat, type: 'flipUp' });
  };

  // Pointer ids that emitted a flipDown. We pair each flipUp with its matching
  // pointer so a stray right-click or pen-eraser-up never queues a phantom
  // flipUp the engine has to reason about.
  private activePointerIds = new Set<number>();

  private onPointerDown = (e: PointerEvent): void => {
    if (!this.active) return;
    // Accept left (button 0) and right (button 2) mouse clicks as flips.
    // Right-click is treated as a second tap surface so players can use
    // either hand or alternate buttons during sustained sections without
    // having to fall back to the keyboard. Middle-click and side buttons
    // (mostly bound to OS / browser navigation) stay reserved.
    if (e.pointerType === 'mouse' && e.button !== 0 && e.button !== 2) return;
    const target = e.target as HTMLElement | null;
    if (target && (target.closest('button') || target.closest('input') || target.closest('a'))) {
      return;
    }
    // Right-click is constrained to the canvas itself so right-clicking
    // header text, leaderboard rows, or anywhere else on the page still
    // opens the standard browser context menu (copy text, open link, etc.).
    // Left-click stays broad to match the long-standing tap behaviour for
    // touch / pen users coming through the same path.
    if (e.pointerType === 'mouse' && e.button === 2 && !target?.closest('canvas')) {
      return;
    }
    this.activePointerIds.add(e.pointerId);
    if (!this.isClockRunning()) return;
    const beat = this.getBeatAtNow();
    this.queue.push({ beat, type: 'flipDown' });
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (!this.active) return;
    if (!this.activePointerIds.delete(e.pointerId)) return;
    if (!this.isClockRunning()) return;
    const beat = this.getBeatAtNow();
    this.queue.push({ beat, type: 'flipUp' });
  };

  private onPointerCancel = (e: PointerEvent): void => {
    this.activePointerIds.delete(e.pointerId);
  };

  // Suppress the browser's context menu only when the right-click landed on
  // the playfield canvas itself, so a right-click flip mid-gameplay doesn't
  // pop the menu. Right-clicks on header text, the leaderboard, or any other
  // page chrome continue to open the normal browser menu for copy / link
  // actions. Anchors / buttons / inputs are also skipped so the standard
  // "Open in new tab" menu still works on links inside the canvas wrapper.
  private onContextMenu = (e: MouseEvent): void => {
    if (!this.active) return;
    if (!this.isClockRunning()) return;
    const target = e.target as HTMLElement | null;
    if (!target?.closest('canvas')) return;
    if (target.closest('button') || target.closest('input') || target.closest('a')) {
      return;
    }
    e.preventDefault();
  };

  private onBlur = (): void => {
    this.heldKeys.clear();
    this.activePointerIds.clear();
  };

  poll(_currentBeat: number): EngineInput[] {
    if (this.queue.length === 0) return [];
    const out = this.queue;
    this.queue = [];
    return out;
  }

  destroy(): void {
    this.active = false;
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerCancel);
    window.removeEventListener('contextmenu', this.onContextMenu);
    window.removeEventListener('blur', this.onBlur);
  }
}

export class ReplayInputSource implements InputSource {
  readonly kind = 'replay' as const;
  private inputs: EngineInput[];
  private cursor = 0;

  constructor(inputs: EngineInput[]) {
    this.inputs = [...inputs].sort((a, b) => a.beat - b.beat);
  }

  poll(currentBeat: number): EngineInput[] {
    const out: EngineInput[] = [];
    while (this.cursor < this.inputs.length && this.inputs[this.cursor].beat <= currentBeat) {
      out.push(this.inputs[this.cursor]);
      this.cursor += 1;
    }
    return out;
  }

  destroy(): void {
    this.inputs = [];
    this.cursor = 0;
  }

  isExhausted(): boolean {
    return this.cursor >= this.inputs.length;
  }
}
